// Pages that load their code only when visited, so the home and landing pages (where ad and search
// visitors arrive) don't download the admin panel, the account pages, Stripe or Google Maps.
//
// Prerendered pages are hydrated (src/index.js), and React throws the prerendered markup away if a
// Suspense boundary appears in it. So index.js calls preload() for the page first, and a page whose
// code is already loaded when it mounts renders directly, without Suspense.
import React, { Suspense, lazy, useState } from 'react';

export function PageLoading() {
  return (
    <div className="lx-page lx-page-loading" role="status" aria-label="Loading">
      <span className="lx-spinner" aria-hidden />
    </div>
  );
}

const RELOAD_KEY = 'rooldrive.chunkReload';

// After a deploy the old code files are gone, and a tab opened before it can't load them. Reload
// once to get the new version instead of showing a broken page.
function reloadOnce(error) {
  try {
    if (sessionStorage.getItem(RELOAD_KEY) !== window.location.pathname) {
      sessionStorage.setItem(RELOAD_KEY, window.location.pathname);
      window.location.reload();
      return new Promise(() => {});
    }
  } catch (e) {
    // storage blocked: fall through and show the error
  }
  throw error;
}

export default function lazyPage(load) {
  let Loaded = null;
  const preload = () => load().then((module) => {
    Loaded = module.default;
    return module;
  });
  const Lazy = lazy(() => preload().catch(reloadOnce));

  function Page(props) {
    // Decided once per mount, so the page isn't remounted when its code finishes loading
    const [direct] = useState(() => Loaded !== null);
    if (direct) return <Loaded {...props} />;
    return (
      <Suspense fallback={<PageLoading />}>
        <Lazy {...props} />
      </Suspense>
    );
  }
  Page.preload = preload;
  return Page;
}
